import React, { useEffect, useState } from "react";
import { Megaphone, CheckCircle2, XCircle, Loader2, PlugZap, Unplug, ShieldCheck, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

const VERIFY_LABELS = {
  data_extensions: "Data Extensions", automations: "Automations", journeys: "Journeys",
};

export default function SfmcConnectPage() {
  const [status, setStatus] = useState(null);
  const [form, setForm] = useState({ subdomain: "", client_id: "", client_secret: "" });
  const [connecting, setConnecting] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [verifyResult, setVerifyResult] = useState(null);

  useEffect(() => {
    api.get("/sfmc-connect/status").then((r) => setStatus(r.data));
  }, []);

  const connect = async (e) => {
    e.preventDefault();
    setConnecting(true);
    try {
      await api.post("/sfmc-connect/connect", form);
      const { data } = await api.get("/sfmc-connect/status");
      setStatus(data);
      toast.success("SFMC org connected successfully!");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't connect — please check your credentials");
    } finally {
      setConnecting(false);
    }
  };

  const disconnect = async () => {
    await api.post("/sfmc-connect/disconnect");
    setStatus({ connected: false });
    setVerifyResult(null);
    toast.success("Disconnected from SFMC org");
  };

  const verify = async () => {
    setVerifying(true);
    try {
      const { data } = await api.post("/sfmc-connect/verify");
      setVerifyResult(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Verification failed");
    } finally {
      setVerifying(false);
    }
  };

  if (!status) return <div className="min-h-screen flex items-center justify-center" data-testid="sfmc-connect-loading"><div className="w-10 h-10 border-2 border-accent border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div className="max-w-2xl mx-auto px-4 sm:px-6 py-8" data-testid="sfmc-connect-page">
      <div className="flex items-center gap-3 mb-2">
        <Megaphone className="w-6 h-6 text-teal-400" />
        <span className="text-xs font-bold uppercase tracking-[0.2em] text-teal-400">Real Org Verification</span>
      </div>
      <h1 className="font-heading text-3xl font-semibold text-white">Connect Your SFMC Org</h1>
      <p className="text-sm text-slate-400 mt-2">Connect your own Salesforce Marketing Cloud org so we can verify — via read-only API calls — that you've actually built Data Extensions, Automations, and Journeys for real, not just in the simulator.</p>

      {status.connected ? (
        <div className="glass-panel rounded-2xl p-6 mt-6" data-testid="sfmc-connected-panel">
          <div className="flex items-center gap-2 text-emerald-400 mb-3">
            <ShieldCheck className="w-5 h-5" /> <span className="font-semibold">Connected</span>
          </div>
          <p className="text-sm text-slate-400">Subdomain: <span className="text-white">{status.subdomain}</span></p>
          <p className="text-sm text-slate-400 mt-1">REST Base: <span className="text-white break-all">{status.rest_instance_url}</span></p>
          <p className="text-sm text-slate-400 mt-1">Connected: <span className="text-white">{new Date(status.connected_at).toLocaleString()}</span></p>

          <div className="flex items-center gap-3 mt-5">
            <button onClick={verify} disabled={verifying} data-testid="sfmc-verify-btn" className="flex items-center gap-2 bg-primary hover:bg-primary/90 text-white text-sm font-semibold px-5 py-2.5 rounded-lg transition-colors">
              {verifying ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />} Verify My Setup
            </button>
            <button onClick={disconnect} data-testid="sfmc-disconnect-btn" className="flex items-center gap-2 text-sm text-rose-400 hover:text-rose-300">
              <Unplug className="w-4 h-4" /> Disconnect
            </button>
          </div>

          {verifyResult && (
            <div className="mt-6 space-y-2.5" data-testid="sfmc-verify-results">
              {Object.entries(verifyResult).map(([key, r]) => (
                <div key={key} className="flex items-start gap-2.5" data-testid={`sfmc-verify-${key}`}>
                  {r.verified ? <CheckCircle2 className="w-4 h-4 text-emerald-400 shrink-0 mt-0.5" /> : <XCircle className="w-4 h-4 text-rose-400 shrink-0 mt-0.5" />}
                  <div>
                    <p className="text-sm text-white">{VERIFY_LABELS[key] || key} {r.verified && `(${r.count} found)`}</p>
                    {r.error && <p className="text-xs text-slate-500 mt-0.5">{r.error}</p>}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      ) : (
        <>
          <div className="glass-panel rounded-2xl p-6 mt-6" data-testid="sfmc-setup-instructions">
            <h3 className="text-sm font-bold text-white mb-3">1. Create a Server-to-Server Installed Package</h3>
            <ol className="text-sm text-slate-400 space-y-1.5 list-decimal list-inside">
              <li>Setup → Platform Tools → Apps → Installed Packages → New</li>
              <li>Add a Component → API Integration → <span className="text-white">Server-to-Server</span></li>
              <li>Grant Read access to: Data Extensions, Automations, and Journeys</li>
              <li>Save, then copy your Client ID, Client Secret, and the subdomain from your Authentication Base URI (the part before ".auth.marketingcloudapis.com")</li>
            </ol>
          </div>

          <form onSubmit={connect} className="glass-panel rounded-2xl p-6 mt-6 space-y-3" data-testid="sfmc-connect-form">
            <h3 className="text-sm font-bold text-white mb-1">2. Enter your Installed Package credentials</h3>
            <input value={form.subdomain} onChange={(e) => setForm((f) => ({ ...f, subdomain: e.target.value }))} placeholder="Subdomain (e.g. mc563885gzs27c5t9-63k636ttgm)" required data-testid="sfmc-subdomain-input" className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2.5 text-sm text-white placeholder:text-slate-500" />
            <input value={form.client_id} onChange={(e) => setForm((f) => ({ ...f, client_id: e.target.value }))} placeholder="Client ID" required data-testid="sfmc-client-id-input" className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2.5 text-sm text-white placeholder:text-slate-500" />
            <input type="password" value={form.client_secret} onChange={(e) => setForm((f) => ({ ...f, client_secret: e.target.value }))} placeholder="Client Secret" required data-testid="sfmc-client-secret-input" className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2.5 text-sm text-white placeholder:text-slate-500" />
            <button type="submit" disabled={connecting} data-testid="sfmc-connect-submit-btn" className="w-full flex items-center justify-center gap-2 bg-primary hover:bg-primary/90 text-white font-semibold px-6 py-3 rounded-lg transition-colors">
              {connecting ? <Loader2 className="w-4 h-4 animate-spin" /> : <><PlugZap className="w-4 h-4" /> Connect to SFMC</>}
            </button>
          </form>
        </>
      )}
    </div>
  );
}
